import React, { useState } from "react";

import ThumbsDownIcon from "@uui/ThumbsDown";
import ThumbsUpIcon from "@uui/ThumbsUp";
import { useNavigate } from "react-router-dom";

import Typography from "@mui/material/Typography";

import ReportFeedbackSnackbar, {
  FeedbackIconButton,
  FeedbackSnackbarHeader,
} from "@app/analytics/components/reports/ReportFeedbackSnackbar";
import useRemoveTopicReport from "@app/analytics/hooks/topics/useRemoveTopicReport";
import useTopicReportFeedback from "@app/analytics/TopicReport/hooks/useTopicReportFeedback";
import type {
  TopicReportFeedback as ITopicReportFeedback,
  TopicReportFeedbackDetails,
} from "@app/analytics/TopicReport/types";
import type { Topic, TopicReportFeedbackKind } from "@app/analytics/types";
import { toast } from "@app/common/hooks/useToasts";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";

import { Link } from "@rui/Link";
import { Row } from "@rui/Stack";

import NegativeFeedbackForm from "./NegativeFeedbackForm";
import { ContentWrapper } from "./styles";

const SubmittedFeedback = ({ onClose }: { onClose: () => void }) => (
  <ContentWrapper flex={1}>
    <FeedbackSnackbarHeader
      title="Thanks for the feedback!"
      subtitle="Your input helps us improve report accuracy."
      icon="success"
      onClose={onClose}
    />
  </ContentWrapper>
);

const ErrorContent = ({ onClose }: { onClose: () => void }) => (
  <ContentWrapper flex={1} spacing={1.5}>
    <FeedbackSnackbarHeader title="Oops!" icon="error" onClose={onClose} />
    <Typography
      variant="bodyPrimaryReg"
      color="shades.neutral.500"
      marginLeft="38px"
    >
      Something went wrong and your feedback could not be saved. Please refresh
      the page or contact{" "}
      <Link href={LINKS.EMAIL.SUPPORT}>support@read.ai</Link> if the problem
      persists.
    </Typography>
  </ContentWrapper>
);

const TopicReportFeedback = ({ topic }: { topic?: Topic }) => {
  const [show, setShow] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [formState, setFormState] = useState<TopicReportFeedbackDetails>({
    reasons: [],
    otherDetail: "",
    action: "none",
  });

  const navigate = useNavigate();
  const { submitFeedback } = useTopicReportFeedback();
  const { removeTopicReport } = useRemoveTopicReport();

  if (!topic) return null;

  const handleClose = () => setShow(false);

  const handleFormFieldChange = (
    values: Partial<TopicReportFeedbackDetails>
  ) => {
    const updatedValues = { ...formState, ...values };
    setFormState(updatedValues);
  };

  const handleRemoveReport = () => {
    removeTopicReport(
      {
        topicId: topic.id,
        ownerUserId: topic.ownerUserId,
      },
      {
        onSuccess: () => {
          navigate(ROUTES.MEETING_REPORTS);
        },
        onError: () => {
          toast.error(
            "Something went wrong and your report could not be removed."
          );
        },
      }
    );
  };

  const handleSubmitFeedback = (values: ITopicReportFeedback) => {
    submitFeedback.mutate(
      {
        topicId: topic.id,
        feedback: {
          entityId: topic.id,
          feedback: values.kind,
          reasons: values.details?.reasons ?? [],
          otherDetail: values.details?.otherDetail,
          kind: "topic",
        },
      },
      {
        onSuccess: () => {
          setShowForm(false);
          setTimeout(() => setShow(false), 2000);
          if (values.details?.action === "remove") {
            handleRemoveReport();
          }
        },
        onError: () => {
          setShowForm(false);
          // longer timeout so the user has ample time to read the error message
          setTimeout(() => setShow(false), 5000);
        },
      }
    );
  };

  const handleClickFeedbackButton = (kind: TopicReportFeedbackKind) => () => {
    if (kind === "positive") {
      handleSubmitFeedback({ kind });
    } else {
      setShowForm(true);
    }
  };

  const handleFormSubmit = () => {
    handleSubmitFeedback({
      kind: "negative",
      details: formState,
    });
  };

  const showFullSnackbar =
    showForm ||
    submitFeedback.isSuccess ||
    submitFeedback.isPending ||
    submitFeedback.isError;

  return (
    <>
      <ReportFeedbackSnackbar show={show && !showFullSnackbar}>
        <Row spacing={1} variant="right">
          <FeedbackIconButton onClick={handleClickFeedbackButton("positive")}>
            <ThumbsUpIcon />
          </FeedbackIconButton>
          <FeedbackIconButton onClick={handleClickFeedbackButton("negative")}>
            <ThumbsDownIcon />
          </FeedbackIconButton>
        </Row>
      </ReportFeedbackSnackbar>
      <ReportFeedbackSnackbar show={show && showFullSnackbar}>
        {showForm ? (
          <NegativeFeedbackForm
            isSubmitting={submitFeedback.isPending}
            onClose={handleClose}
            onChange={handleFormFieldChange}
            onSubmit={handleFormSubmit}
            values={formState}
          />
        ) : submitFeedback.isSuccess ? (
          <SubmittedFeedback onClose={handleClose} />
        ) : submitFeedback.isError ? (
          <ErrorContent onClose={handleClose} />
        ) : null}
      </ReportFeedbackSnackbar>
    </>
  );
};

export default TopicReportFeedback;
